// -----------------------------------------------------------------------------
// src/components/CampaignCatalog.jsx
// FULL component — **vertical scroll** between slides + vertical nav bubbles
// -----------------------------------------------------------------------------

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Box, Typography, Avatar, CircularProgress, Button, Menu, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, Backdrop,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Download as DownloadIcon,
  Google as GoogleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "react-query";

import client from "../../../../API";
import useAuth from "../../../../Hooks/use-auth";
import HomeHeader from "../../../../Components/HomeHeader";
import HomeFooter from "../../../../Components/HomeFooter";
import AlertDialog from "../../../../Components/AlertDialog";
import useAlertDialog from "../../../../Components/useAlertDialog";
import defaultPfp from "../../../../Components/globalAssets/ppfLogo.png";
import BlitzLogo from "../../../../Components/globalAssets/blitzLogo.png-removebg-preview.png";
import { formatMessageContent } from "../../../../Utils/constants";
import PageTitle from "../../../../Components/PageTitle";

/* ────────────────────────────────────────────────────────────────────────── */
/* Presentation primitives                                                  */
/* ────────────────────────────────────────────────────────────────────────── */
const Frame = {
  position: "relative",
  width: "100%",
  height: "100vh",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000",
};

const baseSlideStyle = {
  position: "absolute",
  inset: 0,
  padding: "clamp(1rem,5vw,2rem)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  color: "#fff",
  overflow: "auto",
};

const NavDots = {
  position: "absolute",
  top: "50%",
  right: "1.25rem",
  transform: "translateY(-50%)",
  display: "flex",
  flexDirection: "column",
  gap: 14,
  zIndex: 20,
};

const Dot = (theme, active) => ({
  width: 12,
  height: 12,
  borderRadius: "50%",
  cursor: "pointer",
  background: active
    ? theme.palette.primary.main
    : `${theme.palette.primary.main}55`,
  transition: "transform 0.25s",
  transform: active ? "scale(1.3)" : "scale(1)",
  "&:hover": { transform: "scale(1.3)" },
});

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg,${theme.palette.primary.main} 30%,${theme.palette.secondary.main} 90%)`,
  color: "#fff",
  fontWeight: 700,
  borderRadius: 12,
  padding: "12px 24px",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
  },
}));

const SLIDE_MS = 4500; // auto-advance (except last)

/* ────────────────────────────────────────────────────────────────────────── */
/* Creator card                                                             */
/* ────────────────────────────────────────────────────────────────────────── */
const CreatorCard = ({ creator }) => {
  const navigate = useNavigate();
  const go = () => navigate(`/creators/${creator.name}`);
  const onBtn = (e) => {
    e.stopPropagation();
    go();
  };

  return (
    <Box
      onClick={go}
      sx={{
        cursor: "pointer",
        background:
          "linear-gradient(135deg,rgba(255,255,255,0.08) 0%,rgba(255,255,255,0.04) 100%)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(255,255,255,0.15)",
        borderRadius: 4,
        p: 3,
        width: 220,
        height: 260,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        color: "#fff",
        transition: "transform 0.3s,box-shadow 0.3s",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
        },
      }}
    >
      <Avatar
        src={creator.pfphref || creator.pfpHref || defaultPfp}
        alt={creator.name}
        sx={{ width: 96, height: 96, mb: 1.5 }}
      />
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 700, mt: 0.5, mb: 1, userSelect: "none" }}
      >
        @{creator.name}
      </Typography>

      <Button
        variant="outlined"
        size="small"
        onClick={onBtn}
        sx={{
          mt: "auto",
          color: "#fff",
          borderColor: "rgba(255,255,255,0.4)",
          "&:hover": { borderColor: "#fff" },
          fontWeight: 600,
        }}
      >
        View More
      </Button>
    </Box>
  );
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Main component                                                           */
/* ────────────────────────────────────────────────────────────────────────── */
export default function CampaignCatalog() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  /* responsive breakpoints */
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const creatorsPerSlide = isXs ? 3 : isSm ? 6 : isMd ? 9 : 12;

  /* intro modal */
  const [introOpen, setIntroOpen] = useState(true);

  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();

  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  /* fetch campaign */
  const {
    data: campaign,
    isLoading,
    error,
  } = useQuery(
    ["campaignDetails", campaignId],
    () => client.campaigns.fetch(campaignId),
    { refetchOnWindowFocus: false }
  );

  const totalCreatorPrice = useMemo(
    () =>
      campaign?.creators?.reduce((s, c) => s + Number(c.price || 0), 0) || 0,
    [campaign]
  );

  /* CSV + exports */
  const toCSV = (data) => {
    if (!data?.creators?.length) return "";
    const headers = [
      "Name",
      "Price",
      "Status",
      "Followers",
      "Offer Sent",
      "Assets Sent",
    ];
    const rows = data.creators.map((c) => [
      c.name,
      c.price,
      c.status,
      c.following,
      c.offerSent ? "Yes" : "No",
      c.assetsSent ? "Yes" : "No",
    ]);
    return [headers, ...rows].map((r) => r.join(",")).join("\n");
  };

  const downloadCSV = () => {
    if (!campaign) return;
    const csv = toCSV(campaign);
    const b = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(b);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign_${campaignId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const { mutate: exportToSheets, isLoading: sheetsLoading } = useMutation(
    () =>
      client.campaigns.exportSheet({
        campaignId,
        campaignDetails: campaign,
      }),
    {
      onSuccess: (res) => res.sheetLink && window.open(res.sheetLink, "_blank"),
      onError: (err) =>
        openDialog(
          "Export failed",
          err?.response?.data?.error || err.message,
          closeDialog,
          closeDialog,
          "Close"
        ),
    }
  );

  const { mutate: copyCatalog, isLoading: blitzLoading } = useMutation(
    () => client.campaigns.copyCatalog({ campaignId }),
    {
      onSuccess: (res) =>
        openDialog(
          "Success",
          "Campaign copied to your Blitz!",
          () => navigate(`/editcampaign/${res.new_campaign_id}`),
          closeDialog,
          "Open it",
          "Close"
        ),
      onError: (err) =>
        openDialog(
          "Error",
          err?.response?.data?.error || err.message,
          closeDialog,
          closeDialog,
          "Close"
        ),
    }
  );

  /* slide factories */
  const renderCreatorsSlide = useCallback(
    (chunk) => (
      <Box sx={{ width: "100%", maxWidth: 1100, mx: "auto" }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.8rem", md: "3rem" },
            mb: 3,
          }}
        >
          Featured Creators
        </Typography>
        {chunk?.length ? (
          <Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{ maxWidth: "100%", m: 0 }}
          >
            {chunk.map((c) => (
              <Grid item key={c.id}>
                <CreatorCard creator={c} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography sx={{ opacity: 0.8 }}>
            No creators assigned yet.
          </Typography>
        )}
      </Box>
    ),
    []
  );

  const [exportAnchor, setExportAnchor] = useState(null);

  const renderExportSlide = useCallback(() => {
    if (!campaign) return null;
    const o = campaign.owner || {};
    const avatar =
      o.pfpHref ??
      o.pfphref ??
      o.phphref ??
      o.avatar ??
      o.photo ??
      o.photoUrl ??
      defaultPfp;

    const addToBlitz = () => {
      if (!userInfo) return navigate("/login");
      copyCatalog();
    };

    return (
      <Box
        sx={{
          width: "100%",
          maxWidth: 500,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          textAlign: "center",
        }}
      >
        {campaign.owner && (
          <>
            <Avatar
              src={avatar}
              alt={`${o.first_name || ""} ${o.last_name || ""}`}
              sx={{ width: 120, height: 120, border: "3px solid #fff" }}
            />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Presented by
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, mb: 2, textTransform: "capitalize" }}
            >
              {o.first_name} {o.last_name}
            </Typography>
          </>
        )}

        <Box sx={{ display: "flex", gap: 1 }}>
          <GradientButton
            startIcon={<img src={BlitzLogo} alt="Blitz" style={{ width: 28 }} />}
            onClick={addToBlitz}
            disabled={blitzLoading}
          >
            Add to my Blitz
          </GradientButton>

          <Button
            variant="outlined"
            sx={{ minWidth: 46, px: 1 }}
            onClick={(e) => setExportAnchor(e.currentTarget)}
          >
            <KeyboardArrowDownIcon />
          </Button>
        </Box>
      </Box>
    );
  }, [campaign, userInfo, copyCatalog, blitzLoading, navigate]);

  /* assemble slides */
  const slides = useMemo(() => {
    if (!campaign) return [];

    const intro = {
      bg: "linear-gradient(135deg,#6d00ff,#2d0b63)",
      node: (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              lineHeight: 1.1,
              fontSize: { xs: "2rem", md: "4rem" },
              mb: 1,
            }}
          >
            {campaign.name}
          </Typography>

          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: "#ffe600", mb: 2 }}
          >
            Total Creator Spend: ${totalCreatorPrice.toLocaleString()}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              fontSize: { xs: "1rem", md: "1.25rem" },
              whiteSpace: "pre-wrap",
            }}
          >
            {formatMessageContent(campaign.brief)}
          </Typography>
        </Box>
      ),
    };

    const creatorSlides = [];
    const creators = campaign.creators || [];
    for (let i = 0; i < creators.length; i += creatorsPerSlide) {
      creatorSlides.push({
        bg: "linear-gradient(135deg,#ff4d4d,#7000ff)",
        node: renderCreatorsSlide(creators.slice(i, i + creatorsPerSlide)),
      });
    }

    const exportSlide = {
      bg: "linear-gradient(135deg,#00c9ff,#005bff)",
      node: renderExportSlide(),
    };

    return [intro, ...creatorSlides, exportSlide];
  }, [
    campaign,
    creatorsPerSlide,
    renderCreatorsSlide,
    renderExportSlide,
    totalCreatorPrice,
  ]);

  /* slide index control + wheel scroll */
  const [idx, setIdx] = useState(0);
  const frameRef = useRef(null);
  const lastScrollRef = useRef(0);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      const now = Date.now();
      if (now - lastScrollRef.current < 650) return; // debounce
      lastScrollRef.current = now;
      if (e.deltaY > 40) {
        setIdx((p) => (p + 1) % slides.length);
      } else if (e.deltaY < -40) {
        setIdx((p) => (p - 1 + slides.length) % slides.length);
      }
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [slides.length]);

  /* arrow key support */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowDown") setIdx((p) => (p + 1) % slides.length);
      if (e.key === "ArrowUp") setIdx((p) => (p - 1 + slides.length) % slides.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slides.length]);

  /* auto-advance except last slide */
  const timerRef = useRef(null);
  useEffect(() => {
    clearInterval(timerRef.current);
    if (idx === slides.length - 1) return;
    timerRef.current = setInterval(
      () => setIdx((p) => (p + 1) % slides.length),
      SLIDE_MS
    );
    return () => clearInterval(timerRef.current);
  }, [idx, slides.length]);

  /* --------------------------------------------------------------------- */
  /* render states                                                         */
  /* --------------------------------------------------------------------- */
  if (isLoading)
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error || !campaign)
    return (
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
          color: "#fff",
        }}
      >
        <Typography variant="h6">
          {error ? "Failed to load campaign." : "No campaign data found."}
        </Typography>
      </Box>
    );

  return (
    <>
      <HomeHeader />
      <PageTitle title={`Creator Catalog - ${campaign.name} - Blitz`} />

      {/* intro popup */}
      <Dialog open={introOpen} onClose={() => setIntroOpen(false)} maxWidth="sm">
        <DialogTitle>Welcome to Your Catalog</DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body1" gutterBottom>
            This catalog is a <strong>time-limited activation</strong> — a
            curated collection of creators selected <em>specifically</em> with
            you in mind. Scroll or use the arrows to flip through slides, meet
            featured creators, and export everything in a click.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIntroOpen(false)} variant="contained">
            Let’s Go
          </Button>
        </DialogActions>
      </Dialog>

      {/* presentation frame */}
      <Box ref={frameRef} sx={Frame}>
        {slides.map((s, i) => (
          <Box
            key={i}
            sx={{
              ...baseSlideStyle,
              background: s.bg,
              gap: 24,
              transform: `translateY(${(i - idx) * 100}%)`,
              transition: "transform 0.8s ease-in-out",
            }}
          >
            {s.node}
          </Box>
        ))}

        {/* nav dots (vertical) */}
        <Box sx={NavDots}>
          {slides.map((_, i) => (
            <Box
              key={i}
              sx={Dot(theme, i === idx)}
              onClick={() => setIdx(i)}
            />
          ))}
        </Box>
      </Box>

      <HomeFooter />

      {/* export menu */}
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (!userInfo) return navigate("/login");
            downloadCSV();
            setExportAnchor(null);
          }}
        >
          <DownloadIcon sx={{ mr: 1 }} />
          CSV Export
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!userInfo) return navigate("/login");
            exportToSheets();
            setExportAnchor(null);
          }}
        >
          <GoogleIcon sx={{ mr: 1 }} />
          Google Sheets
        </MenuItem>
      </Menu>

      {/* backdrop while exporting / copying */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
        open={sheetsLoading || blitzLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <AlertDialog alertState={dialogState} />
    </>
  );
}
